/**
 * Contact Page
 * Allows users to submit contact inquiries
 * Pre-fills name and email for logged-in users
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Send, CheckCircle, AlertCircle, Loader2, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { contactApi } from '../services/api';

type SubjectType = 'general' | 'support' | 'billing' | 'feedback' | 'other';

const subjectOptions: { value: SubjectType; label: string }[] = [
  { value: 'general', label: 'General Inquiry' },
  { value: 'support', label: 'Product Support' },
  { value: 'billing', label: 'Billing Question' },
  { value: 'feedback', label: 'Feedback' },
  { value: 'other', label: 'Other' },
];

export default function ContactPage(): JSX.Element {
  const { currentUser } = useAuth();

  // Pre-fill name and email if user is logged in
  const [name, setName] = useState(currentUser?.displayName || '');
  const [email, setEmail] = useState(currentUser?.email || '');
  const [subject, setSubject] = useState<SubjectType>('general');
  const [message, setMessage] = useState('');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError('');

    // Validation
    if (!name.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!message.trim()) {
      setError('Please enter your message');
      return;
    }

    if (message.trim().length < 20) {
      setError('Please provide more details in your message (at least 20 characters)');
      return;
    }

    setLoading(true);

    try {
      await contactApi.submit({
        name: name.trim(),
        email: email.trim(),
        subject,
        message: message.trim(),
      });

      setSubmitted(true);
    } catch (err: any) {
      console.error('Contact form error:', err);
      setError(err.message || 'Failed to submit your message. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  function handleSendAnother(): void {
    setSubmitted(false);
    setMessage('');
    setSubject('general');
    setError('');
  }

  return (
    <div className="container-custom py-12">
      <div className="max-w-2xl mx-auto">
        <div className="card p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-primary-100 dark:bg-primary-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <MessageSquare size={32} className="text-primary-600 dark:text-primary-400" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">
              Contact Us
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Have a question or feedback? We'd love to hear from you.
            </p>
          </div>

          {/* Success State */}
          {submitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle size={32} className="text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
                Message Sent!
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Thank you for reaching out. We'll get back to you as soon as possible.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={handleSendAnother}
                  className="btn btn-primary inline-flex items-center justify-center gap-2"
                >
                  <Send size={18} />
                  Send Another Message
                </button>
                <Link
                  to="/"
                  className="btn btn-secondary inline-flex items-center justify-center gap-2"
                >
                  <ArrowLeft size={18} />
                  Back to Home
                </Link>
              </div>
            </div>
          ) : (
            <>
              {/* Error Message */}
              {error && (
                <div className="flex items-start gap-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
                  <AlertCircle size={20} className="flex-shrink-0 mt-0.5" />
                  <span className="text-sm">{error}</span>
                </div>
              )}

              {/* Contact Form */}
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name Field */}
                <div>
                  <label htmlFor="name" className="block text-sm font-medium mb-2">
                    Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Your name"
                    required
                    className="input"
                  />
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="input"
                  />
                </div>

                {/* Subject Field */}
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium mb-2">
                    Subject <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value as SubjectType)}
                    required
                    className="input"
                  >
                    {subjectOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Message Field */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="How can we help you?"
                    required
                    rows={5}
                    className="input min-h-[150px] resize-y"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Minimum 20 characters
                  </p>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Message
                    </>
                  )}
                </button>
              </form>

              {/* Additional Info */}
              <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  You can also check our{' '}
                  <Link
                    to="/help"
                    className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                  >
                    Help Center
                  </Link>{' '}
                  for quick answers to common questions.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
